/**
 * Shared popover surface for the @ mention menu, / slash menu, and inline date picker.
 *
 * Anchored absolutely against the viewport (caret rect or chip rect supplied by caller).
 * Click-outside and Escape dismiss; mousedown listener uses capture phase so it beats
 * the editor's own handlers.
 */

import Box from '@mui/material/Box'
import GlobalStyles from '@mui/material/GlobalStyles'
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

export interface PopoverPosition {
   /** Caret/chip top-left in viewport coordinates */
   left: number
   top: number
   /** Bottom of caret/chip; popover is placed below this by default */
   bottom: number
}

interface Props {
   anchor: PopoverPosition
   width?: number
   maxHeight?: number
   onDismiss: () => void
   children: React.ReactNode
}

const POPOVER_GAP = 6
const VIEWPORT_EDGE_PADDING = 8
const SHADOW_8 = '0 5px 5px -3px rgba(0,0,0,0.20), 0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12)'

export default function PopoverSurface({ anchor, width = 280, maxHeight = 360, onDismiss, children }: Props) {
   const surfaceRef = useRef<HTMLDivElement | null>(null)
   const setSurfaceRef = useCallback((element: HTMLDivElement | null) => { surfaceRef.current = element }, [])

   useEffect(() => {
      const handleMouseDown = (event: MouseEvent) => {
         if (surfaceRef.current && !surfaceRef.current.contains(event.target as Node)) {
            onDismiss()
         }
      }
      document.addEventListener('mousedown', handleMouseDown, true)
      return () => document.removeEventListener('mousedown', handleMouseDown, true)
   }, [onDismiss])

   useLayoutEffect(() => {
      const surfaceElement = surfaceRef.current
      if (!surfaceElement) return
      let top = anchor.bottom + POPOVER_GAP
      let left = anchor.left
      const surfaceRect = surfaceElement.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      // Flip above the anchor if the popover would overflow the viewport bottom and there's room above.
      if (top + surfaceRect.height > viewportHeight && anchor.top - surfaceRect.height - POPOVER_GAP > 0) {
         top = anchor.top - surfaceRect.height - POPOVER_GAP
      }
      // Shift left so the popover stays inside the viewport.
      if (left + surfaceRect.width > viewportWidth - VIEWPORT_EDGE_PADDING) {
         left = Math.max(VIEWPORT_EDGE_PADDING, viewportWidth - surfaceRect.width - VIEWPORT_EDGE_PADDING)
      }
      surfaceElement.style.top = `${top}px`
      surfaceElement.style.left = `${left}px`
      surfaceElement.style.visibility = 'visible'
   })

   return createPortal(
      <>
         <GlobalStyles styles={{
            '@keyframes gapPopIn': {
               from: { opacity: 0, transform: 'translateY(-4px) scale(0.98)' },
               to: { opacity: 1, transform: 'translateY(0) scale(1)' },
            },
         }} />
         <Box
            ref={setSurfaceRef}
            role="listbox"
            sx={{
               position: 'fixed',
               left: 0,
               top: 0,
               visibility: 'hidden',
               width,
               maxHeight,
               background: '#FFFFFF',
               borderRadius: '8px',
               boxShadow: SHADOW_8,
               padding: '4px 0',
               overflow: 'hidden',
               display: 'flex',
               flexDirection: 'column',
               zIndex: 1300,
               animation: 'gapPopIn 120ms cubic-bezier(0.4, 0, 0.2, 1) both',
            }}
         >
            {children}
         </Box>
      </>,
      document.body,
   )
}
